<template>
  <div>
    <div v-if="isLoading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando concursos...</span>
      </div>
      <p class="mt-3">Carregando concursos, aguarde...</p>
    </div>

    <div v-else-if="emptyListPublications" class="alert alert-info mt-4">
      Nenhum concurso disponível no momento.
    </div>

    <div v-else>
      <div v-for="publication in publications" :key="publication.id" class="author-list-area">
      <div class="title">
        {{ publication.titulo }}
      </div>

      <div class="author">
        {{ publication.descricao_prazo_inscricao }}
        <br><br>
        {{ publication.descricao_duvidas }}
        <b><a :href="'mailto:' + emailSuporte">{{ emailSuporte }}</a></b>
      </div>

      <br>

      <div class="desc">{{ publication.prefeitura_descricao}}</div>

      <div :class="['verticalTxt', extrairNomePrefeitura(publication.prefeitura) === 'Vicentina' ? 'verticalTxtActive' : 'verticalTxtInactive']">
        <div class="rotateTxtCon">
            <p>{{ extrairNomePrefeitura(publication.prefeitura) }}</p>
        </div>
      </div>

      <div class="btnGroup">
        <button :class="['btn', 'btn-sm', getStatusButtonClass(publication.status)]"
                type="submit"
                style="margin-right: 10px;">
          {{ publication.status }}
        </button>

        <button class="btn btn-link-data btn-info btn-sm"
                data-toggle="collapse" 
                :data-target="'#collapsePublications' + publication.id"
                aria-expanded="false" 
                :aria-controls="'collapsePublications' + publication.id"
                v-if="publication.results && publication.results.length">
            Publicações
        </button>

        <button class="btn btn-link-data btn-info btn-sm"
                data-toggle="collapse" 
                :data-target="'#collapseLinks' + publication.id"
                aria-expanded="false" 
                :aria-controls="'collapseLinks' + publication.id">
            Links e Arquivos
        </button>
      </div>

      <div class="collapse collapse-links" :id="'collapsePublications' + publication.id">
        <div class="card card-header">
            Publicações
        </div>
        <div class="card card-body">
          <table class="table table-striped">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Publicação</th>
              </tr>
            </thead>
            <tbody>
                <tr v-for="result in publication.results" :key="result.id">
                  <th scope="row">{{ dateConvert(result.data_publicacao) }}</th>
                  <td>
                    <a v-if="result.link && result.link !== '#'" :href="result.link" target="_blank">
                      {{ result.titulo }}
                    </a>
                    <span v-else>{{ result.titulo }}</span>
                  </td>
                </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="collapse collapse-links" :id="'collapseLinks' + publication.id">
        <div class="card card-header">
            Links e Arquivos
        </div>

        <div class="card card-body">
          <ul class="list-group list-group-flush">
            <li v-if="publication.link_area_candidato" class="list-group-item link-item">
                <a :href="publication.link_area_candidato" target="_blank">
                  Área do candidato
                </a>
            </li>
            <li v-else class="list-group-item">
                <span class="text-muted">Link não disponível</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
export default {
    data() {
        return {
            publications: [],
            emailSuporte: 'suporte@ibcgp.org.br',
            emptyListPublications: false,
            isLoading: false,
        }
    },

    created() {
        this.fetchPublications();
    },

    methods: {
        /**
         * Caminho relativo da API (axios usa baseURL). Corrige registros legados
         * que apontam para /publications/... em vez de /geral/publications/...
         */
        normalizeAxiosRequestPath(path) {
            if (!path || typeof path !== 'string') return '';
            let s = path.trim();
            if (/^https?:\/\//i.test(s)) return s;
            if (!s.startsWith('/')) s = `/${s}`;
            if (s.startsWith('/publications') && !s.startsWith('/geral/publications')) {
                s = `/geral${s}`;
            }
            return s;
        },

        /** Link absoluto para PDF/arquivo ou URL externa (href no template). */
        normalizeAssetHref(href) {
            if (!href || href === '#') return '';
            const s = String(href).trim();
            if (/^https?:\/\//i.test(s)) return s;
            if (s.startsWith('//')) return `https:${s}`;
            const base = String(this.$axios.defaults.baseURL || '').replace(/\/$/, '');
            if (s.startsWith('/')) return `${base}${s}`;
            return `${base}/${s}`;
        },

        /**
         * Área do candidato: costuma ser URL completa ou caminho no app de concursos.
         * Caminhos relativos à API (/ps, /geral, /storage) usam baseURL do axios; demais relativos usam o site de inscrições.
         */
        normalizeExternalHref(href) {
            if (!href) return null;
            const s = String(href).trim();
            if (/^https?:\/\//i.test(s)) return s;
            if (s.startsWith('//')) return `https:${s}`;
            if (s.startsWith('mailto:') || s.startsWith('tel:')) return s;
            const apiBase = String(this.$axios.defaults.baseURL || '').replace(/\/$/, '');
            let path = s.startsWith('/') ? s : `/${s}`;
            if (path.startsWith('/ps/') || path.startsWith('/geral/') || path.startsWith('/storage')) {
                return `${apiBase}${path}`;
            }
            const concursosOrigin = 'https://concursos.ibcgp.org.br';
            return `${concursosOrigin}${path}`;
        },

        mapPublicationRowItem(item) {
            const raw = item.caminho_arquivo || item.url || item.link || '';
            return {
                id: item.id,
                titulo: item.titulo || item.arquivo || 'Publicação',
                data_publicacao: item.data_publicacao || item.created_at,
                link: this.normalizeAssetHref(raw),
            };
        },

        async fetchPublications() {
          this.isLoading = true;
          this.emptyListPublications = false;
          try {
            // Buscar concursos do sistema legado e novo em paralelo
            const [legadoResponse, novoResponse] = await Promise.allSettled([
                this.$axios.get(`/geral/publications`),
                this.$axios.get(`/ps/concurso`)
            ]);

            let publicationsLegado = [];
            let concursosNovos = [];

            // Processar resposta do sistema legado
            if (legadoResponse.status === 'fulfilled') {
                publicationsLegado = legadoResponse.value.data.sort((a, b) => b.id - a.id);
            } else {
                console.error("Erro ao buscar publicações do sistema legado:", legadoResponse.reason);
            }

            // Processar resposta do novo sistema
            if (novoResponse.status === 'fulfilled') {
                concursosNovos = novoResponse.value.data || [];
                
                // Transformar concursos do novo sistema para o formato esperado
                const concursosTransformados = await this.transformarConcursosNovos(concursosNovos);
                publicationsLegado = publicationsLegado.concat(concursosTransformados);
            } else {
                console.error("Erro ao buscar concursos do novo sistema:", novoResponse.reason);
            }

            // Ordenar todos por id (decrescente)
            this.publications = publicationsLegado.sort((a, b) => b.id - a.id);

            // Para cada publicação, buscar as publicações relacionadas
            this.publications.forEach(publication => {
                this.fetchPublicationDetails(publication);
            });

            this.emptyListPublications = this.publications.length === 0;
          } catch (error) {
              console.error("Erro ao buscar as publicações:", error);
              this.emptyListPublications = true;
          } finally {
              this.isLoading = false;
          }
        },

        async transformarConcursosNovos(concursos) {
            // Processar concursos em paralelo para melhor performance
            const promises = concursos.map(async (concurso) => {
                try {
                    // Buscar publicações do concurso
                    let publicacoes = [];
                    try {
                        const publicacoesResponse = await this.$axios.get(`/ps/concurso/${concurso.id}/publicacoes`);
                        const rawList = publicacoesResponse.data;
                        const arr = Array.isArray(rawList) ? rawList : (rawList && rawList.data ? rawList.data : []);
                        publicacoes = arr.map(pub => this.mapPublicationRowItem(pub));
                    } catch (error) {
                        console.warn(`Erro ao buscar publicações do concurso ${concurso.id}:`, error);
                        publicacoes = [];
                    }

                    let status = 'EM ANDAMENTO';
                    if (concurso.status_inscricoes === 'ENCERRADAS') {
                        status = 'INSCRIÇÕES ENCERRADAS';
                    } else if (concurso.status_inscricoes === 'AGUARDANDO') {
                        status = 'AGUARDANDO';
                    } else if (concurso.status_inscricoes === 'ABERTAS') {
                        status = 'EM ANDAMENTO';
                    } else {
                        const hoje = new Date();
                        if (concurso.data_prazo_fim) {
                            const dataFim = new Date(concurso.data_prazo_fim);
                            if (hoje > dataFim) {
                                status = 'INSCRIÇÕES ENCERRADAS';
                            }
                        }
                    }

                    // Criar descrição do prazo de inscrição
                    let descricaoPrazo = '';
                    if (concurso.data_prazo_inicio && concurso.data_prazo_fim) {
                        const dataInicio = this.formatarDataBrasileira(concurso.data_prazo_inicio);
                        const dataFim = this.formatarDataBrasileira(concurso.data_prazo_fim);
                        descricaoPrazo = `Período de inscrição: de ${dataInicio} até ${dataFim}`;
                    } else if (concurso.data_prazo_fim) {
                        const dataFim = this.formatarDataBrasileira(concurso.data_prazo_fim);
                        descricaoPrazo = `Prazo para inscrição até ${dataFim}`;
                    }

                    // Criar descrição da prefeitura
                    let prefeituraDescricao = '';
                    if (concurso.prefeitura) {
                        // Extrair apenas o nome principal da prefeitura (antes de " - " ou "/")
                        const nomePrefeitura = this.extrairNomePrefeitura(concurso.prefeitura);
                        prefeituraDescricao = nomePrefeitura;
                        if (concurso.titulo && !concurso.titulo.includes(nomePrefeitura)) {
                            prefeituraDescricao += ` - ${concurso.titulo}`;
                        }
                    }

                    // Extrair nome principal da prefeitura para exibição no texto vertical
                    const nomePrefeituraPrincipal = this.extrairNomePrefeitura(concurso.prefeitura || 'Não informado');

                    // Transformar para o formato esperado
                    const concursoTransformado = {
                        id: concurso.id + 1000000, // Offset maior para não conflitar com IDs do legado
                        titulo: concurso.titulo || 'Concurso',
                        prefeitura: nomePrefeituraPrincipal, // Usar apenas o nome principal
                        prefeitura_original: concurso.prefeitura || 'Não informado', // Manter original para referência
                        prefeitura_descricao: prefeituraDescricao,
                        descricao_prazo_inscricao: descricaoPrazo,
                        descricao_duvidas: 'Possui alguma dúvida? Entre em contato conosco: ',
                        status: status,
                        link_area_candidato: this.normalizeExternalHref(concurso.link_area_candidato),
                        link_publicacoes: concurso.link_publicacoes
                            ? this.normalizeAxiosRequestPath(concurso.link_publicacoes)
                            : `/ps/concurso/${concurso.id}/publicacoes`,
                        results: publicacoes, // Já buscar as publicações aqui
                        isNovoSistema: true // Flag para identificar concursos do novo sistema
                    };

                    return concursoTransformado;
                } catch (error) {
                    console.error(`Erro ao transformar concurso ${concurso.id}:`, error);
                    return null;
                }
            });

            const resultados = await Promise.all(promises);
            return resultados.filter(item => item !== null); // Remover itens com erro
        },

        formatarDataBrasileira(data) {
            if (!data) return '';
            
            try {
                // Se a data vem no formato YYYY-MM-DD
                if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}/.test(data)) {
                    const date = new Date(data);
                    const dia = String(date.getDate()).padStart(2, '0');
                    const mes = String(date.getMonth() + 1).padStart(2, '0');
                    const ano = date.getFullYear();
                    return `${dia}/${mes}/${ano}`;
                }
                
                // Outros formatos
                const date = new Date(data);
                const dia = String(date.getDate()).padStart(2, '0');
                const mes = String(date.getMonth() + 1).padStart(2, '0');
                const ano = date.getFullYear();
                return `${dia}/${mes}/${ano}`;
            } catch (error) {
                return data;
            }
        },

        fetchPublicationDetails(publication) {
            // Se já tem results (concursos do novo sistema já foram transformados), não precisa buscar novamente
            if (publication.results && publication.isNovoSistema) {
                return;
            }

            // Para concursos do legado, buscar as publicações
            if (publication.link_publicacoes) {
                const reqPath = this.normalizeAxiosRequestPath(publication.link_publicacoes);
                if (!reqPath) {
                    this.$set(publication, 'results', []);
                    return;
                }
                this.$axios.get(reqPath)
                    .then(response => {
                        const raw = response.data;
                        const list = Array.isArray(raw) ? raw : (raw && raw.data ? raw.data : []);
                        const results = list.map(item => this.mapPublicationRowItem(item));
                        this.$set(publication, 'results', results);
                    })
                    .catch(error => {
                        console.error("Erro ao buscar os detalhes da publicação:", error);
                        this.$set(publication, 'results', []);
                    });
            }
        },

        dateConvert(inputDate) {
            if (!inputDate) return '';
            
            // Se já está no formato brasileiro, retornar como está
            if (typeof inputDate === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(inputDate)) {
                return inputDate;
            }
            
            try {
                // Tentar parsear como data ISO ou formato YYYY-MM-DD
                let date;
                if (typeof inputDate === 'string' && inputDate.includes('T')) {
                    // Formato ISO com hora
                    date = new Date(inputDate);
                } else if (typeof inputDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(inputDate)) {
                    // Formato YYYY-MM-DD
                    const aux = inputDate.split("-");
                    return `${aux[2]}/${aux[1]}/${aux[0]}`;
                } else {
                    date = new Date(inputDate);
                }
                
                // Verificar se a data é válida
                if (isNaN(date.getTime())) {
                    // Tentar formato antigo como fallback
                    const aux = String(inputDate).split("-");
                    if (aux.length === 3) {
                        return `${aux[2]}/${aux[1]}/${aux[0]}`;
                    }
                    return inputDate;
                }
                
                // Formatar para DD/MM/YYYY
                const dia = String(date.getDate()).padStart(2, '0');
                const mes = String(date.getMonth() + 1).padStart(2, '0');
                const ano = date.getFullYear();
                return `${dia}/${mes}/${ano}`;
            } catch (error) {
                console.warn('Erro ao converter data:', inputDate, error);
                return inputDate;
            }
        },

        getButtonStatus(dataPrazoFim) {
            if (new Date() > new Date(dataPrazoFim)) {
                return 'INSCRIÇÕES ENCERRADAS';
            }
            return 'EM ANDAMENTO';
        },

        getStatusButtonClass(status) {
            if (!status) return 'btn-secondary';
            const s = String(status).toUpperCase();
            if (s.includes('ENCERR') || s === 'ENCERRADO') {
                return 'btn-danger';
            }
            if (s.includes('AGUARD')) {
                return 'btn-warning';
            }
            return 'btn-success';
        },

        extrairNomePrefeitura(prefeitura) {
            if (!prefeitura) return 'Não informado';
            
            // Remove espaços extras
            let nome = prefeitura.trim();
            
            // Remove padrões como " - 02/2025", " MS - 02/2025", "/2025", etc.
            // Primeiro, tenta remover padrões com hífen seguido de números/data
            nome = nome.replace(/\s*-\s*\d{2}\/\d{4}.*$/i, ''); // Remove " - 02/2025" ou similar
            nome = nome.replace(/\s*-\s*\d{4}.*$/i, ''); // Remove " - 2025" ou similar
            
            // Remove padrões com barra seguida de números
            nome = nome.replace(/\s*\/\s*\d{2}\/\d{4}.*$/i, ''); // Remove "/02/2025" ou similar
            nome = nome.replace(/\s*\/\s*\d{4}.*$/i, ''); // Remove "/2025" ou similar
            
            // Remove sufixos comuns de estado (MS, SP, etc.) se estiverem no final após espaço
            nome = nome.replace(/\s+(MS|SP|RJ|MG|PR|SC|RS|BA|GO|PE|CE|DF|ES|PB|AL|SE|RN|PI|MA|PA|AM|RO|AC|AP|RR|TO|MT)$/i, '');
            
            // Remove qualquer coisa após hífen no final (caso ainda reste algo)
            const partes = nome.split(' - ');
            if (partes.length > 1) {
                // Se a primeira parte parece ser um nome válido (não é só número/data), usa ela
                const primeiraParte = partes[0].trim();
                if (primeiraParte && !/^\d+$/.test(primeiraParte)) {
                    nome = primeiraParte;
                }
            }
            
            return nome.trim() || prefeitura.trim(); // Retorna o nome limpo ou o original se ficou vazio
        }
    }
}
</script>

<style scoped>
  .btn-link-data{
    margin-right: 10px;
  }

  .title {
    font-size:18px;
    color:#333;
    margin-bottom: 20px;
  }
  .author {
    font-size:16px;
    color:#777777;
  }
  .desc {
    font-size:14px;
    color:#a4a4a4;
    margin-bottom: 20px;
  }
  .list-area {
    margin-top:25px;  
  }
  .author-list-area {
    position:relative;
    padding-left:40px;
    padding-bottom:10px;
    margin-bottom:40px;  
    border-bottom:solid 1px #ccc;
  }
  .verticalTxt {  
    position:absolute;
    top:0;
    left:0;
    text-align:center;
    padding:5px;
    width:30px;
    height:100%;
  }
  .verticalTxtActive{
    background: #618579;
  }

  .verticalTxtInactive{
    background: #856761;
  }

  .rotateTxtCon {
    position:relative;
    width:100%;
    height:100%;
    text-align:center;
  }
  .rotateTxtCon p {
    position:absolute;
    transform: translate(-50%,-50%) rotate(-90deg);
    top:50%;
    left:50%;
    font-size:20px;
    text-transform:uppercase;
    color: white;
  }
  .btnGroup {
    margin:10px 0 0;
  }

  .collapse-links{
    margin-top: 10px;
  }

  .link-item{
    color: #007bff !important
  }
</style>